<script lang="ts">
	import type { Snippet } from 'svelte';
	import { cn } from '../../utils.js';
	import { setFormContext, type FieldRegistration } from './form-context.svelte.js';
	import { getFormInvalidFallback } from './invalid-fallback.js';

	let {
		id,
		onsubmit,
		onInvalid,
		onBeforeSubmit,
		submitOnEnter = false,
		children,
		class: className
	}: {
		id?: string;
		onsubmit?: (() => void) | (() => Promise<void>);
		onInvalid?: () => void;
		onBeforeSubmit?: () => boolean;
		submitOnEnter?: boolean;
		children: Snippet;
		class?: string;
	} = $props();

	const fields = new Map<string, FieldRegistration>();
	let showAll = $state(false);

	setFormContext({
		register(name: string, field: FieldRegistration) {
			fields.set(name, field);
		},
		unregister(name: string) {
			fields.delete(name);
		},
		showAllErrors() {
			return showAll;
		}
	});

	export function touchAll(): void {
		showAll = true;
		for (const field of fields.values()) {
			field.touch();
		}
	}

	function checkIsValid(): boolean {
		for (const field of fields.values()) {
			if (field.getError() !== null) return false;
		}
		return true;
	}

	function checkIsTouched(): boolean {
		for (const field of fields.values()) {
			if (field.isTouched()) return true;
		}
		return false;
	}

	export function validate(): boolean {
		touchAll();
		return checkIsValid();
	}

	export function validateTab(tabValue: string): boolean {
		let valid = true;
		for (const field of fields.values()) {
			if (field.tabValue !== tabValue) continue;
			field.touch();
			if (field.getError() !== null) valid = false;
		}
		return valid;
	}

	export function getErrors(): Record<string, string> {
		const errors: Record<string, string> = {};
		for (const [name, field] of fields) {
			const error = field.getError();
			if (error) errors[name] = error;
		}
		return errors;
	}

	export function getFirstErrorTab(): string | undefined {
		for (const field of fields.values()) {
			if (field.getError() !== null) return field.tabValue;
		}
		return undefined;
	}

	export function getFirstErrorField(tabValue?: string): FieldRegistration | undefined {
		for (const field of fields.values()) {
			if (tabValue !== undefined && field.tabValue !== tabValue) continue;
			if (field.getError() !== null) return field;
		}
		return undefined;
	}

	export function reset(): void {
		showAll = false;
		for (const field of fields.values()) {
			field.reset();
		}
	}

	export function isValid(): boolean {
		return checkIsValid();
	}

	export function isTouched(): boolean {
		return checkIsTouched();
	}

	function handleSubmit(e: SubmitEvent) {
		e.preventDefault();
		if (onBeforeSubmit && !onBeforeSubmit()) {
			return;
		}
		if (!validate()) {
			if (onInvalid) {
				onInvalid();
			} else {
				getFormInvalidFallback()();
			}
			return;
		}
		onsubmit?.();
	}

	let formElement: HTMLFormElement | undefined = $state();

	export function getElement(): HTMLFormElement | undefined {
		return formElement;
	}

	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'Enter') {
			if (e.ctrlKey || e.metaKey) {
				e.preventDefault();
				formElement?.requestSubmit();
			} else {
				const target = e.target as HTMLElement;
				if (submitOnEnter && target.tagName !== 'TEXTAREA' && target.tagName !== 'BUTTON') {
					e.preventDefault();
					formElement?.requestSubmit();
				} else if (target.tagName !== 'BUTTON' && target.tagName !== 'TEXTAREA') {
					e.preventDefault();
				}
			}
		}
	}
</script>

<form {id} bind:this={formElement} onsubmit={handleSubmit} onkeydown={handleKeydown} tabindex="-1" class={cn('outline-none', className)}>
	{@render children()}
</form>
